import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Eye, Bot, Monitor, Smartphone, Tablet, Globe, ArrowDownUp, RefreshCw, Share2 } from "lucide-react";
import { api } from "../../lib/api";
import { PageTitle } from "./Layout";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";

const COUNTRY_NAMES = {
  TR: "Türkiye", US: "ABD", DE: "Almanya", GB: "İngiltere", FR: "Fransa", NL: "Hollanda",
  BE: "Belçika", AT: "Avusturya", CH: "İsviçre", AZ: "Azerbaycan", CY: "KKTC", RU: "Rusya",
  IT: "İtalya", ES: "İspanya", GR: "Yunanistan", BG: "Bulgaristan", RO: "Romanya",
  SE: "İsveç", NO: "Norveç", DK: "Danimarka", FI: "Finlandiya", CA: "Kanada", AU: "Avustralya",
};
const DEVICE_LABEL = { desktop: "Masaüstü", phone: "Telefon", tablet: "Tablet", bot: "Bot" };
const BROWSER_LABEL = { chrome: "Chrome", safari: "Safari", firefox: "Firefox", edge: "Edge", opera: "Opera", other: "Diğer" };

const KpiCard = ({ icon: Icon, label, value, hint, testid }) => (
  <Card className="p-4 bg-white border" data-testid={testid}>
    <div className="flex items-center gap-2 text-stone-500 text-xs uppercase tracking-wide"><Icon size={14} /> {label}</div>
    <div className="font-serif text-3xl font-bold mt-1 tabular-nums">{value}</div>
    {hint && <div className="text-xs text-stone-400 mt-1">{hint}</div>}
  </Card>
);

function MiniSparkline({ series, metric = "visits" }) {
  const w = 720, h = 160, pad = 24, topPad = 36;
  const maxV = Math.max(1, ...series.map((d) => d[metric]));
  const stepX = (w - pad * 2) / Math.max(series.length - 1, 1);
  const pts = series.map((d, i) => [pad + i * stepX, h - pad - (d[metric] / maxV) * (h - topPad - pad)]);
  const path = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
  const area = path + ` L ${pts[pts.length - 1][0].toFixed(1)},${h - pad} L ${pad},${h - pad} Z`;
  const showDots = series.length <= 60;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" role="img" aria-label="Günlük ziyaret grafiği" data-testid="analytics-sparkline">
      <defs><linearGradient id="g1" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#DC2626" stopOpacity="0.18" /><stop offset="100%" stopColor="#DC2626" stopOpacity="0" /></linearGradient></defs>
      {[0.25, 0.5, 0.75].map((p) => <line key={p} x1={pad} x2={w - pad} y1={topPad + (h - topPad - pad) * p} y2={topPad + (h - topPad - pad) * p} stroke="#e7e5e4" strokeDasharray="3 3" />)}
      <path d={area} fill="url(#g1)" />
      <path d={path} stroke="#DC2626" strokeWidth="2" fill="none" />
      {showDots && pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={2.5} fill="#DC2626" />)}
      <text x={pad} y={h - 6} fontSize="10" fill="#78716c">{series[0]?.day?.slice(5)}</text>
      <text x={w - pad} y={h - 6} fontSize="10" fill="#78716c" textAnchor="end">{series[series.length - 1]?.day?.slice(5)}</text>
      <text x={pad} y={14} fontSize="11" fill="#44403c" fontWeight="600">{`Zirve: ${maxV}`}</text>
    </svg>
  );
}

function BarList({ title, items, labelKey = "key", total, formatter = (x) => x, icon: Icon, testid, maxItems = 10, countKey = "visits", subKey = "visitors" }) {
  const max = Math.max(1, ...items.map((it) => it[countKey]));
  return (
    <Card className="p-4 bg-white border" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">{Icon && <Icon size={15} className="text-stone-500" />} {title}</h3>
        <span className="text-xs text-stone-400">Toplam: {total}</span>
      </div>
      {!items.length && <p className="text-sm text-stone-400 py-6 text-center">Veri yok</p>}
      <ul className="space-y-1.5">
        {items.slice(0, maxItems).map((it) => (
          <li key={String(it[labelKey])} className="relative group">
            <div className="flex items-center justify-between text-xs mb-0.5 relative z-10">
              <span className="truncate pr-2 font-medium" title={String(it[labelKey])}>{formatter(it[labelKey])}</span>
              <span className="tabular-nums text-stone-600">{it[countKey]}{subKey && it[subKey] != null && <> <span className="text-stone-400">({it[subKey]})</span></>}</span>
            </div>
            <div className="h-2 bg-stone-100 rounded-sm overflow-hidden">
              <div className="h-full bg-red-500/70 transition-all" style={{ width: `${(it[countKey] / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
      {items.length > maxItems && <p className="text-xs text-stone-400 text-center mt-2">+{items.length - maxItems} daha</p>}
    </Card>
  );
}

const SHARE_PLATFORM_LABEL = { x: "X (Twitter)", twitter: "X (Twitter)", facebook: "Facebook", whatsapp: "WhatsApp", linkedin: "LinkedIn", telegram: "Telegram", copy: "Link Kopyala" };

export default function AnalyticsAdmin() {
  const [days, setDays] = useState(30);
  const [includeBots, setIncludeBots] = useState(false);
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["admin", "analytics", days, includeBots],
    queryFn: () => api.get(`/admin/analytics/summary?days=${days}&include_bots=${includeBots}`).then((r) => r.data),
    refetchOnWindowFocus: false,
  });
  const t = data?.totals;
  const visitorAvg = useMemo(() => (data?.series?.length ? (t.visits / data.series.length).toFixed(1) : "0"), [data, t]);
  return (
    <>
      <PageTitle
        title="İstatistikler"
        sub="Site trafiği, ziyaretçi, cihaz ve kaynak dağılımı. Veriler sunucuda tutulur — çerez yok."
        action={<Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} data-testid="analytics-refresh"><RefreshCw size={14} className={isFetching ? "animate-spin" : ""} /> Yenile</Button>}
      />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[[7, "Son 7 gün"], [30, "Son 30 gün"], [90, "Son 90 gün"], [365, "Son 1 yıl"]].map(([v, l]) => (
          <Button key={v} size="sm" variant={days === v ? "default" : "outline"} onClick={() => setDays(v)} data-testid={`analytics-range-${v}`}>{l}</Button>
        ))}
        <label className="flex items-center gap-2 text-sm ml-auto cursor-pointer" data-testid="analytics-bots-toggle">
          <input type="checkbox" checked={includeBots} onChange={(e) => setIncludeBots(e.target.checked)} className="rounded" /> Botları dahil et
        </label>
      </div>
      {!data ? <p className="text-sm text-stone-400">Yükleniyor…</p> : (
        <div className="space-y-5" data-testid="analytics-dashboard">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard icon={Eye} label="Toplam Görüntüleme" value={t.visits.toLocaleString("tr-TR")} hint={`Günlük ort. ${visitorAvg}`} testid="kpi-visits" />
            <KpiCard icon={Users} label="Benzersiz Ziyaretçi" value={t.unique_visitors.toLocaleString("tr-TR")} hint="IP başına günlük 1 kez" testid="kpi-visitors" />
            <KpiCard icon={Share2} label="Paylaşım Tıklaması" value={(t.share_clicks ?? 0).toLocaleString("tr-TR")} hint="Sosyal medya + kopyala" testid="kpi-shares" />
            <KpiCard icon={Bot} label={includeBots ? "Bot Görüntüleme" : "Filtrelenen Bot"} value={t.bots_filtered.toLocaleString("tr-TR")} hint={includeBots ? "Grafiklere dahil" : "Hariç tutuldu"} testid="kpi-bots" />
          </div>
          <Card className="p-4 bg-white border">
            <h3 className="font-semibold text-sm mb-2">Günlük Trafik ({data.window_days} gün)</h3>
            <MiniSparkline series={data.series} />
          </Card>
          <div className="grid gap-4 md:grid-cols-2">
            <BarList title="En Çok Ziyaret Edilen Sayfalar" items={data.top_paths} total={t.visits} icon={Eye} testid="list-top-paths" />
            <BarList title="Trafik Kaynakları" items={data.top_referrers} total={data.top_referrers.reduce((a, b) => a + b.visits, 0)} icon={Globe} testid="list-referrers" formatter={(k) => k || "Doğrudan"} />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <BarList title="Ülke Dağılımı" items={data.top_countries} total={data.top_countries.reduce((a, b) => a + b.visits, 0)} icon={Globe} testid="list-countries" formatter={(k) => `${k} · ${COUNTRY_NAMES[k] || "Bilinmiyor"}`} maxItems={12} />
            <BarList title="Cihazlar" items={data.devices} total={data.devices.reduce((a, b) => a + b.visits, 0)} icon={Smartphone} testid="list-devices" formatter={(k) => DEVICE_LABEL[k] || k} />
            <BarList title="Tarayıcılar" items={data.browsers} total={data.browsers.reduce((a, b) => a + b.visits, 0)} icon={Monitor} testid="list-browsers" formatter={(k) => BROWSER_LABEL[k] || k} />
          </div>
          {(data.share_platforms?.length > 0 || data.top_shares?.length > 0) && (
            <div className="grid gap-4 md:grid-cols-2">
              <BarList title="Paylaşım Platformu Dağılımı" items={data.share_platforms || []} total={t.share_clicks ?? 0} icon={Share2} testid="list-share-platforms" formatter={(k) => SHARE_PLATFORM_LABEL[k] || k} countKey="clicks" subKey={null} />
              <BarList title="En Çok Paylaşılan İçerikler" items={data.top_shares || []} total={t.share_clicks ?? 0} icon={Share2} testid="list-top-shares" formatter={(k) => { try { const u = new URL(k); return u.pathname.replace(/^\/api\/public\/share\//, "/") || "/"; } catch { return k; } }} countKey="clicks" subKey={null} maxItems={8} />
            </div>
          )}
          <p className="text-xs text-stone-400 flex items-center gap-2"><Tablet size={12} /> Çerez kullanılmaz; benzersiz ziyaretçi günlük tuzlanmış IP özeti ile ölçülür. Veriler yalnızca bu panelde görünür.</p>
        </div>
      )}
    </>
  );
}
